import { type ColumnFiltersState } from '@tanstack/react-table'
import React, { useEffect, useRef, useState } from 'react'

export const DEBOUNCE_DELAY_MS = 500

export function TextFilter<T> ({
  columnFilters,
  setColumnFilters,
  property,
  placeholder,
  dataTestId,
}: {
  columnFilters: ColumnFiltersState;
  setColumnFilters: (arg: ColumnFiltersState) => void;
  property: keyof T;
  placeholder?: string;
  dataTestId?: string;
}) {
  const initialValue = columnFilters.find((filter) => filter.id === property)
    ?.value as string
  const [inputValue, setInputValue] = useState(initialValue ?? '')
  const prevInputValueRef = useRef(inputValue)

  useEffect(() => {
    // Check if inputValue has changed
    if (inputValue === prevInputValueRef.current) {
      return // inputValue hasn't changed, so exit the effect early
    }

    const handler = setTimeout(() => {
      if (inputValue !== '') {
        setColumnFilters([
          { id: property as string, value: inputValue },
          ...columnFilters.filter((filter) => filter.id !== property),
        ])
      } else {
        setColumnFilters(
          columnFilters.filter((filter) => filter.id !== property)
        )
      }
    }, DEBOUNCE_DELAY_MS)

    prevInputValueRef.current = inputValue

    return () => {
      clearTimeout(handler)
    }
  }, [inputValue, columnFilters, property, setColumnFilters])

  return (
    <input
      className='input-default'
      type='text'
      data-testid={dataTestId ?? `input-text-${property as string}`}
      value={inputValue}
      onChange={(event) => {
        event.preventDefault()
        setInputValue(event.target.value)
      }}
      placeholder={placeholder}
    />
  )
}
